/**
 * Small fetch helper for the customer's own data (account, jobs, orders, assets).
 * Base URL from config/api.js; auth token from localStorage (set by AuthContext).
 */
import { API_BASE_URL } from '../config/api'
import { getToken, reportAuthFailure } from './session'

export async function api(path, { method = 'GET', body } = {}) {
  const token = getToken()
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    reportAuthFailure(res.status, data)
    throw new Error(data.error || `Request failed (${res.status})`)
  }
  return data
}

export const accountApi = {
  summary: () => api('/api/customer-app/account'),
  paymentMethods: () => api('/api/customer-app/account/payment-methods'),
  setDefaultPaymentMethod: id => api(`/api/customer-app/account/payment-methods/${id}/default`, { method: 'PATCH' }),
  removePaymentMethod: id => api(`/api/customer-app/account/payment-methods/${id}/remove`, { method: 'POST' }),
  assets: (status = 'active') => api(`/api/customer-app/account/assets${status === 'retired' ? '?status=retired' : ''}`),
  addAsset: body => api('/api/customer-app/account/assets', { method: 'POST', body }),
  updateAsset: (id, body) => api(`/api/customer-app/account/assets/${id}`, { method: 'PATCH', body }),
  retireAsset: id => api(`/api/customer-app/account/assets/${id}/retire`, { method: 'POST' }),
  addresses: () => api('/api/customers/addresses'),
  setDefaultAddress: id => api(`/api/customers/addresses/${id}/default`, { method: 'PATCH' }),
  updateProfile: body => api('/api/customers/profile', { method: 'PATCH', body })
}

export const jobsApi = {
  orders: () => api('/api/customer-app/jobs'),
  upcoming: () => api('/api/customer-app/jobs/upcoming'),
  activity: (limit = 10) => api(`/api/customer-app/jobs/activity?limit=${limit}`),
  job: id => api(`/api/customer-app/jobs/${id}`),
  createRequest: body => api('/api/customer-app/jobs/requests', { method: 'POST', body }),
  reportIssue: (id, body) => api(`/api/customer-app/jobs/${id}/issue`, { method: 'POST', body }),
  // Job lifecycle actions (routes/pro-jobs.js, /api/customer/*)
  acceptQuote: id => api(`/api/customer/quotes/${id}/accept`, { method: 'POST' }),
  decideVariation: (id, approve) => api(`/api/customer/variations/${id}/decide`, { method: 'POST', body: { approve } }),
  signOff: (id, body) => api(`/api/customer/jobs/${id}/sign-off`, { method: 'POST', body })
}

/** Plain-language labels for job / request statuses */
export const STATUS_LABEL = {
  REQUESTED: 'Request submitted', PENDING: 'Waiting for a professional', QUOTED: 'Quote received', ACCEPTED: 'Quote accepted',
  SCHEDULED: 'Visit scheduled', ASSIGNED: 'Professional assigned', CONFIRMED: 'Professional confirmed', EN_ROUTE: 'On the way',
  ON_SITE: 'Arrived', IN_PROGRESS: 'Work in progress', PAUSED: 'Work paused', AWAITING_RETURN: 'Next visit pending',
  COMPLETED: 'Work completed', APPROVED: 'Signed off', DISPUTED: 'Issue reported', CANCELLED: 'Cancelled', REJECTED: 'Declined'
}
export const statusLabel = s => STATUS_LABEL[s] || (s ? s.replace(/_/g, ' ').toLowerCase() : '—')

export function timeAgo(date) {
  if (!date) return ''
  const s = Math.round((Date.now() - new Date(date).getTime()) / 1000)
  const future = s < 0
  const a = Math.abs(s)
  const [n, unit] = a < 60 ? [a, 'second'] : a < 3600 ? [Math.round(a / 60), 'minute'] : a < 86400 ? [Math.round(a / 3600), 'hour']
    : a < 2592000 ? [Math.round(a / 86400), 'day'] : a < 31536000 ? [Math.round(a / 2592000), 'month'] : [Math.round(a / 31536000), 'year']
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(future ? n : -n, unit)
}

/** Title-case a setting value such as market.default_city ('beirut' → 'Beirut') */
export const titleCase = v => (v ? String(v).replace(/\b\w/g, c => c.toUpperCase()) : '')
